import { storeToRefs } from 'pinia';

import {
  PatchedCompanyUpdateRequest,
  PatchedTemplateUpdateRequest,
  PatchedTraceabilityDriverCreateUpdateInputRequest,
  PatchedTraceabilityVehicleCreateUpdateInputRequest,
  PolymorphicTransfer,
  TraceabilityApiFactory,
  TraceabilityApiTraceabilityCompaniesPartialUpdateRequest,
  TraceabilityApiTraceabilityCompanyCustomersRetrieveRequest,
  TraceabilityApiTraceabilityCompanyLicensesCreateRequest,
  TraceabilityApiTraceabilityDriversCreateRequest,
  TraceabilityApiTraceabilityDriversListRequest,
  TraceabilityApiTraceabilityItemsListRequest,
  TraceabilityApiTraceabilityTemplatesCreateRequest,
  TraceabilityApiTraceabilityTemplatesListRequest,
  TraceabilityApiTraceabilityTemplatesRetrieveRequest,
  TraceabilityApiTraceabilityTransfersCreateRequest,
  TraceabilityApiTraceabilityTransfersListRequest,
  TraceabilityApiTraceabilityVehiclesListRequest,
  TraceabilityVehicleCreateUpdateInputRequest,
  TransferCreateInputRequest,
} from '@/clients/unifiedApi';
import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { OrderSummaryOrder } from '@/models/order';
import { Product } from '@/models/product';
import {
  ApiConfig,
  GetAvailableTagsParams,
  GetItemsParams,
  GetPackagesParams,
  GetProductsParams,
  GetStrainsArgs,
  GetTraceabilityLocationsParams,
  MetrcStrain,
  PackageStatistics,
  PackageStatisticsParams,
  ProductPackageFilters,
  RefreshRequest,
  RefreshRequestParams,
  SeedToSaleItem,
  SeedToSalePackage,
  SeedToSalePermissionsResponse,
  SeedToSaleProduct,
  SplitPackageParams,
  SplitPackageResponse,
  TraceabilityBulkImportProductsParams,
  TraceabilityBuyerPackage,
  TraceabilityCategory,
  TraceabilityCategoryListParams,
  TraceabilityLocation,
  TraceabilityProductStatistics,
  TraceabilitySettings,
  TransferAPIParams,
  TransferApiResponse,
  UpdateProductPackageFiltersParams,
} from '@/models/traceability';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import { buildPath } from '@/utils';
import JSONTransform from '@/utils/json-transform';

import { apiSlugSession, createSession } from '../session';
import {
  ManifestMatchesForOrders,
  RefreshResourcesRequestParams,
  SuggestedManifestRequest,
} from './models/traceability';
import { getUnifiedApi } from './unifiedApi';

// region 'internal-api'

export const getTraceabilitySettings = async () => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.get<TraceabilitySettings>(URLS.TRACEABILITY.SETTINGS, { params });

  return response.data;
};

export const getPackages = async ({ serialized = '', ...extraQueryParams }: GetPackagesParams) => {
  const url = URLS.TRACEABILITY.PACKAGES;
  const params = new JSONTransform({
    errorFormat: 'jsonapi',
    ...extraQueryParams,
  }).toSnakeCaseKeys();
  const apiSession = createSession(
    () => {
      const userStore = useUserStore();

      return URLS.INTERNAL + userStore.userActiveCompany?.slug;
    },
    { arrayFormat: 'comma' },
  );

  const response = await apiSession.get<PaginatedResponse<SeedToSalePackage>>(`${url}?${serialized}`, {
    params,
  });

  return response.data;
};

export const getBuyerPackages = async (params: GetPackagesParams) => {
  const payload = new JSONTransform({
    errorFormat: 'jsonapi',
    ...params,
  }).toSnakeCaseKeys();

  const session = apiSlugSession;

  const response = await session.get<PaginatedResponse<TraceabilityBuyerPackage>>(URLS.TRACEABILITY.PACKAGES, {
    params: payload,
  });

  return response.data;
};

export const getTraceabilityItems = async (params: GetItemsParams) => {
  params.error_format = 'jsonapi';
  const response = await apiSlugSession.get<PaginatedResponse<SeedToSaleItem>>(URLS.TRACEABILITY.ITEMS, {
    params,
  });

  return response.data;
};

export const getUnifiedTraceabilityItemsList = async (params: TraceabilityApiTraceabilityItemsListRequest) => {
  const api = await getUnifiedApi(TraceabilityApiFactory);
  const response = await api.traceabilityItemsList(params, { params: { error_format: 'jsonapi' } });

  return response.data;
};

export const updatePackage = async (packageToUpdate: Partial<SeedToSalePackage>) => {
  const { id, ...payload } = packageToUpdate;
  const params = { error_format: 'jsonapi' };

  const response = await apiSlugSession.patch(`${URLS.TRACEABILITY.PACKAGES}/${id}?${URLS.CUSTOM_ERROR}`, payload, {
    params,
  });

  return response.data;
};

export const splitPackage = async (payload: SplitPackageParams) => {
  const params = { error_format: 'jsonapi' };

  const response = await apiSlugSession.post<SplitPackageResponse>(URLS.TRACEABILITY.PACKAGES, payload, { params });

  return response.data;
};

/**
 * Assigns multiple products to packages
 * @returns newly created products assigned to package
 */
export const updatePackages = async (packages: Partial<SeedToSalePackage>[]) => {
  const params = { error_format: 'jsonapi' };

  const response = await apiSlugSession.patch(
    `${URLS.TRACEABILITY.PACKAGES}/bulk_update?${URLS.CUSTOM_ERROR}`,
    packages,
    { params },
  );

  return response.data;
};

export const deleteApiConfig = async (integrationId: ApiConfig['integrationId']) => {
  const url = `${URLS.TRACEABILITY.SETTINGS}/${integrationId}`;
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.delete(url, { params });

  return response.data;
};

export const getTraceabilityLocations = async ({
  license_number,
  ignore_excluded,
  limit = 1000,
}: GetTraceabilityLocationsParams = {}) => {
  const params = {
    error_format: 'jsonapi',
    ignore_excluded,
    license_number: license_number?.join(','),
    limit,
  };
  const response = await apiSlugSession.get<PaginatedResponse<TraceabilityLocation>>(`${URLS.TRACEABILITY.LOCATIONS}`, {
    params,
  });

  return response.data;
};

export const updateIncludedLocations = async ({ included_location_ids }: { included_location_ids: number[] }) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.patch(
    URLS.TRACEABILITY.INCLUDED_LOCATIONS,
    { included_location_ids },
    { params },
  );

  return response.data;
};

export const queueRefreshProductsPackages = async (productIds) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.patch(URLS.TRACEABILITY.ACTIONS.REFRESH_PRODUCTS_PACKAGES, productIds, {
    params,
  });

  return response.data;
};

export const queueRefreshResources = async (params: RefreshResourcesRequestParams) => {
  const response = await apiSlugSession.get(URLS.TRACEABILITY.ACTIONS.REFRESH_RESOURCES, {
    params: {
      error_format: 'jsonapi',
      end_date: params.endDate,
      start_date: params.startDate,
      types: params.types.join(','),
    },
  });

  return response.data;
};

export const getProductPackageFilters = async (productIds: Array<Product['id']>) => {
  const response = await apiSlugSession.get<ProductPackageFilters>(`${URLS.TRACEABILITY.PRODUCT_PACKAGE_FILTERS}`, {
    params: { error_format: 'jsonapi', ll_product_ids: productIds.join(',') },
  });

  return response.data;
};

export const updateProductPackageFilters = async (payload: UpdateProductPackageFiltersParams) => {
  const response = await apiSlugSession.post(`${URLS.TRACEABILITY.PRODUCT_PACKAGE_FILTERS}`, payload, {
    params: { error_format: 'jsonapi' },
  });

  return response.data;
};

/**
 * Delete the entire product package filter for a given product id
 */
export const deleteProductPackageFilter = async (productId: Product['id']) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.delete(`${URLS.TRACEABILITY.PRODUCT_PACKAGE_FILTERS}/${productId}`, { params });

  return response.data;
};

export const getProducts = async ({ serialized = '', ...extraQueryParams }: GetProductsParams) => {
  const url = URLS.TRACEABILITY.PRODUCTS;
  const params = new JSONTransform({
    errorFormat: 'jsonapi',
    ...extraQueryParams,
  }).toSnakeCaseKeys();

  const apiSession = createSession(
    () => {
      const userStore = useUserStore();

      return URLS.INTERNAL + userStore.userActiveCompany?.slug;
    },
    { arrayFormat: 'comma' },
  );

  const response = await apiSession.get<PaginatedResponse<SeedToSaleProduct>>(`${url}/?${serialized}`, {
    params,
  });

  return response.data;
};

export const getProduct = async (id: SeedToSaleProduct['id']) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.get<SeedToSaleProduct>(`${URLS.TRACEABILITY.PRODUCTS}/${id}/`, { params });

  return response.data;
};

export const getRefreshRequests = async (params?: RefreshRequestParams) => {
  const { serialized = '', ...p } = params || {};
  p.error_format = 'jsonapi';

  const response = await apiSlugSession.get<PaginatedResponse<RefreshRequest>>(
    `${URLS.TRACEABILITY.REFRESH_REQUESTS}?${serialized}`,
    { params: new JSONTransform(p).toSnakeCaseKeys() },
  );

  return response.data;
};

export const getTransfers = async (params: TransferAPIParams) => {
  params.error_format = 'jsonapi';
  const response = await apiSlugSession.get<PaginatedResponse<TransferApiResponse>>(URLS.TRACEABILITY.TRANSFERS, {
    params: new JSONTransform(params).toSnakeCaseKeys(),
  });

  return response.data;
};

export const getActiveCompanyS2SStaffPermission = async (id: number) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.get<SeedToSalePermissionsResponse>(`${URLS.TRACEABILITY.STAFF}/${id}`, {
    params,
  });

  return response.data;
};

export const postCreateProductFromItem = async (payload: SeedToSaleItem) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.post(
    buildPath(URLS.TRACEABILITY.NEW_PRODUCT, { itemId: payload.id }),
    payload,
    { params },
  );

  return response.data;
};

export const getStrains = async (params: GetStrainsArgs) => {
  params.error_format = 'jsonapi';
  const response = await apiSlugSession.get<PaginatedResponse<MetrcStrain>>(URLS.TRACEABILITY.STRAINS, {
    params,
  });

  return response.data;
};

export const getAvailableTags = async (params: GetAvailableTagsParams) => {
  params.error_format = 'jsonapi';
  const response = await apiSlugSession.get<PaginatedResponse<string>>(URLS.TRACEABILITY.AVAILABLE_TAGS, {
    params: new JSONTransform(params).toSnakeCaseKeys(),
  });

  return response.data;
};

export const getProductsPackageQuantities = async (productIdList: number[]) => {
  const productIds = productIdList.join(',');

  const response = await apiSlugSession.get<TraceabilityProductStatistics>(URLS.TRACEABILITY.PRODUCTS_STATISTICS, {
    params: { error_format: 'jsonapi', product_ids: productIds },
  });

  return response.data;
};

export const getTransferTypes = async () => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.get(URLS.TRACEABILITY.TRANSFER_TYPES, { params });

  return response.data;
};

export const attachPackagesToTemplate = async (templateId: number, packageIds: number[]) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.patch(
    buildPath(URLS.TRACEABILITY.ATTACH_PACKAGES_TEMPLATE, { templateId }),
    {
      package_ids: packageIds,
    },
    { params },
  );

  return response.data;
};

export const deletePackagesFromTemplate = async (params: {
  error_format?: string;
  templateId: number;
  packageId: number;
}) => {
  params.error_format = 'jsonapi';
  const response = await apiSlugSession.delete(buildPath(URLS.TRACEABILITY.DELETE_PACKAGE_TEMPLATE, params));

  return response.data;
};

export const attachManifestsToOrder = async (order: OrderSummaryOrder, transferExternalIds: (number | string)[]) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.put(
    buildPath(URLS.TRACEABILITY.ORDER_MANIFESTS, { orderNumber: order.number }),
    {
      traceability_transfer_external_ids: transferExternalIds,
    },
    { params },
  );

  return response.data;
};

export const getAttachedManifestsForOrder = async (
  order: OrderSummaryOrder,
  {
    full = false,
    transfer_type = 'outbound',
  }: { full?: boolean; transfer_type?: 'outbound' | 'inbound' | 'pending' } = {},
) => {
  const response = await apiSlugSession.get(
    buildPath(URLS.TRACEABILITY.ORDER_MANIFESTS, { orderNumber: order.number }),
    {
      params: {
        error_format: 'jsonapi',
        full,
        transfer_type,
      },
    },
  );

  return response.data;
};

export const attachOrderToManifest = async (transferId: number | string, orderNumber: string) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.put(
    buildPath(URLS.TRACEABILITY.ATTACH_ORDERS_TO_MANIFEST, { transferId }),
    {
      orders: [orderNumber],
    },
    { params },
  );

  return response.data;
};

export const getSuggestedManifestMatchesForOrders = async (orderNumbers: string[] = []) => {
  const params = {
    error_format: 'jsonapi',
  };
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const response = await session.post<ManifestMatchesForOrders>(
    URLS.TRACEABILITY.SUGGESTED_MANIFEST_MATCHES,
    orderNumbers,
    {
      params,
    },
  );

  return response.data;
};

export const getSuggestedManifest = async (payload: SuggestedManifestRequest) => {
  const params = {
    error_format: 'jsonapi',
  };
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const response = await session.post<PolymorphicTransfer>(URLS.TRACEABILITY.SUGGESTED_MANIFEST, payload, { params });

  return response.data;
};

export const getPackagesStatistics = async (params: PackageStatisticsParams) => {
  const session = apiSlugSession;
  params.error_format = 'jsonapi';

  const response = await session.get<PackageStatistics>(
    buildPath(URLS.TRACEABILITY.STATISTICS, { moduleName: 'packages' }),
    {
      params: new JSONTransform(params).toSnakeCaseKeys(),
    },
  );

  return response.data;
};

export const getLabTestResultPDF = async (packageId: number) => {
  const params = { error_format: 'jsonapi' };
  const response = await apiSlugSession.get(buildPath(URLS.TRACEABILITY.LAB_TEST_RESULTS_PDF, { packageId }), {
    params,
  });
  return response.data;
};

// region: 'unified-generated'

export async function getTemplate(payload: TraceabilityApiTraceabilityTemplatesRetrieveRequest) {
  const api = await getUnifiedApi(TraceabilityApiFactory);
  const params = { error_format: 'jsonapi' };
  const response = await api.traceabilityTemplatesRetrieve(payload, { params });

  return response.data;
}

export async function getTemplates(payload: TraceabilityApiTraceabilityTemplatesListRequest) {
  const api = await getUnifiedApi(TraceabilityApiFactory);
  const params = { error_format: 'jsonapi' };
  const response = await api.traceabilityTemplatesList(payload, { params });

  return response.data;
}

export async function getTraceabilityCompanyCustomer(
  payload: TraceabilityApiTraceabilityCompanyCustomersRetrieveRequest,
) {
  const api = await getUnifiedApi(TraceabilityApiFactory);
  const params = { error_format: 'jsonapi' };
  const response = await api.traceabilityCompanyCustomersRetrieve(payload, { params });

  return response.data;
}

export async function getTraceabilityCompanyCustomers() {
  const { activeCompany } = useCompanyStore();

  if (!activeCompany) {
    return;
  }

  const api = await getUnifiedApi(TraceabilityApiFactory);
  const params = { error_format: 'jsonapi' };
  const response = await api.traceabilityCompanyCustomersList(
    {
      companyId: activeCompany.id,
    },
    { params },
  );

  return response.data;
}

export async function getDrivers(search?: string) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const requestParams = {
    companyId: Number(activeCompanyId.value),
    search,
  } as TraceabilityApiTraceabilityDriversListRequest;

  const options = {
    params: { error_format: 'jsonapi' },
  };

  const response = await api.traceabilityDriversList(requestParams, options);

  return response.data;
}

export async function postDriver(payload) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const requestParams = {
    companyId: Number(activeCompanyId.value),
    traceabilityDriverCreateUpdateInputRequest: payload,
  } as TraceabilityApiTraceabilityDriversCreateRequest;
  const options = {
    params: { error_format: 'jsonapi' },
  };

  const { data } = await api.traceabilityDriversCreate(requestParams, options);
  return data;
}

export async function postManifest(payload: TransferCreateInputRequest) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const requestParams = {
    companyId: Number(activeCompanyId.value),
    transferCreateInputRequest: payload,
  } as TraceabilityApiTraceabilityTransfersCreateRequest;
  const options = {
    params: { error_format: 'jsonapi' },
  };

  const { data } = await api.traceabilityTransfersCreate(requestParams, options);
  return data;
}

export async function patchDriver({
  externalId,
  payload,
}: {
  externalId: string;
  payload: PatchedTraceabilityDriverCreateUpdateInputRequest;
}) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const requestParams = {
    companyId: Number(activeCompanyId.value),
    externalId,
    patchedTraceabilityDriverCreateUpdateInputRequest: payload,
  };
  const options = {
    params: { error_format: 'jsonapi' },
  };

  const { data } = await api.traceabilityDriversPartialUpdate(requestParams, options);
  return data;
}

export async function getVehicles(search?: string) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);
  const requestParams = {
    companyId: Number(activeCompanyId.value),
    search,
  } as TraceabilityApiTraceabilityVehiclesListRequest;
  const options = {
    params: { error_format: 'jsonapi' },
  };

  const response = await api.traceabilityVehiclesList(requestParams, options);

  return response.data;
}

export async function postVehicle(payload: TraceabilityVehicleCreateUpdateInputRequest) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const requestParams = {
    companyId: Number(activeCompanyId.value),
    traceabilityVehicleCreateUpdateInputRequest: payload,
  };
  const options = {
    params: { error_format: 'jsonapi' },
  };

  const { data } = await api.traceabilityVehiclesCreate(requestParams, options);
  return data;
}

export async function patchVehicle({
  externalId,
  payload,
}: {
  externalId: string;
  payload: PatchedTraceabilityVehicleCreateUpdateInputRequest;
}) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const requestParams = {
    companyId: Number(activeCompanyId.value),
    externalId,
    patchedTraceabilityVehicleCreateUpdateInputRequest: payload,
  };
  const options = {
    params: { error_format: 'jsonapi' },
  };

  const { data } = await api.traceabilityVehiclesPartialUpdate(requestParams, options);
  return data;
}

export async function patchTemplate(templateId: number, templatePayload: PatchedTemplateUpdateRequest) {
  const { activeCompany } = useCompanyStore();

  if (!activeCompany) {
    return;
  }

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const payload = {
    companyId: activeCompany.id,
    id: templateId,
    patchedTemplateUpdateRequest: templatePayload,
  };
  const params = { error_format: 'jsonapi' };

  const { data } = await api.traceabilityTemplatesPartialUpdate(payload, { params });
  return data;
}

export async function postTemplate(payload: TraceabilityApiTraceabilityTemplatesCreateRequest) {
  const api = await getUnifiedApi(TraceabilityApiFactory);
  const params = { error_format: 'jsonapi' };
  const response = await api.traceabilityTemplatesCreate(payload, { params });

  return response.data;
}

export const getTransferCompaniesList = async (payloadData) => {
  const { activeCompany } = useCompanyStore();

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const payload = {
    ...payloadData,
    companyId: activeCompany?.id,
  };
  const params = { error_format: 'jsonapi' };

  const response = await api.traceabilityTransferCompaniesList(payload, { params });

  return response.data;
};

export async function updateTraceabilitySettings(patchedCompanyUpdateRequest: PatchedCompanyUpdateRequest) {
  const { activeCompany } = useCompanyStore();

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const payload = {
    companyId: activeCompany?.id,
    id: activeCompany?.id,
    patchedCompanyUpdateRequest,
  } as TraceabilityApiTraceabilityCompaniesPartialUpdateRequest;
  const params = { error_format: 'jsonapi' };

  const response = await api.traceabilityCompaniesPartialUpdate(payload, { params });

  return response.data;
}

export async function validateApiKey({ apiKey, companyId }: { apiKey: string; companyId?: number }) {
  const { activeCompany } = useCompanyStore();

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const payload = {
    companyId: companyId ?? activeCompany?.id,
    licenseApiKeyRequest: {
      api_key: apiKey,
    },
  } as TraceabilityApiTraceabilityCompanyLicensesCreateRequest;
  const params = { error_format: 'jsonapi' };

  const response = await api.traceabilityCompanyLicensesCreate(payload, { params });

  return response.data;
}

export async function getUnifiedTransfers(req_params: Partial<TraceabilityApiTraceabilityTransfersListRequest>) {
  const { activeCompany } = useCompanyStore();

  const api = await getUnifiedApi(TraceabilityApiFactory);

  const payload = {
    ...req_params,
    companyId: activeCompany?.id,
  } as TraceabilityApiTraceabilityTransfersListRequest;
  const params = { error_format: 'jsonapi' };

  const response = await api.traceabilityTransfersList(payload, { params });

  return response.data;
}

export const getTraceabilityCategories = async (_params: TraceabilityCategoryListParams = {}) => {
  const params = new JSONTransform({
    error_format: 'jsonapi',
    ..._params,
  }).toSnakeCaseKeys();

  const response = await apiSlugSession.get<PaginatedResponse<TraceabilityCategory>>(URLS.TRACEABILITY.CATEGORIES, {
    params,
  });

  return response.data;
};

export const postBulkImportProducts = async (payload: TraceabilityBulkImportProductsParams) => {
  const params = { error_format: 'jsonapi' };
  await apiSlugSession.post<void>(URLS.TRACEABILITY.BULK_IMPORTS, payload, { params });
};
